const BASE_URL = 'https://kalimni-backend-568669174420.me-central1.run.app';

const getCurrentLang = (): 'ar' | 'en' => {
  if (typeof window === 'undefined') return 'ar';
  return window.location.pathname.startsWith('/en') ? 'en' : 'ar';
};

const setCookie = (name: string, value: string, days = 7) => {
  if (typeof window === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=None; Secure`;
};

const mapLanguageCode = (lang: string): string => {
  const l = lang?.toLowerCase() || '';
  if (l.includes('arab') || l === 'ar' || l === 'العربية') return 'ar';
  if (l.includes('eng')  || l === 'en' || l === 'English')  return 'en';
  return 'ar';
};

const getAuthHeaders = () => {
  let token = null;
  if (typeof window !== 'undefined') {
    token = localStorage.getItem('auth_token') || localStorage.getItem('userToken');
  }
  return {
    'Content-Type': 'application/json',
    'Accept-Language': getCurrentLang(),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

const getPublicHeaders = () => ({
  'Content-Type': 'application/json',
  'Accept-Language': getCurrentLang(),
});

const getSpecialistId = (): string => {
  if (typeof window === 'undefined') return '';
  try {
    const userInfoStr = localStorage.getItem('user_info');
    if (!userInfoStr) return '';
    const info = JSON.parse(userInfoStr);
    return String(info.id || info._id || info.authUid || info.uid || '');
  } catch { return ''; }
};

const getCurrentUserInfo = (): {
  id: string; role: string; fullName: string;
  username: string; displayName: string; email: string;
} => {
  if (typeof window === 'undefined') {
    return { id: '', role: '', fullName: '', username: '', displayName: '', email: '' };
  }
  try {
    const userInfoStr = localStorage.getItem('user_info');
    if (!userInfoStr) return { id: '', role: '', fullName: '', username: '', displayName: '', email: '' };
    const info        = JSON.parse(userInfoStr);
    const fullName    = info.fullName || info.name || info.displayName || '';
    const username    = (info.username || info.userName || '').trim();
    const displayName = username || fullName;
    return {
      id:          String(info.id || info._id || info.authUid || info.uid || ''),
      role:        (info.role || '').toLowerCase(),
      fullName, username, displayName,
      email: info.email || '',
    };
  } catch { return { id: '', role: '', fullName: '', username: '', displayName: '', email: '' }; }
};

const LIKED_KEY = 'kalimni_liked_posts';

const getLikedPostsFromStorage = (): Record<string, boolean> => {
  if (typeof window === 'undefined') return {};
  try { return JSON.parse(localStorage.getItem(LIKED_KEY) || '{}'); } catch { return {}; }
};

const setLikedInStorage = (postId: string, liked: boolean) => {
  if (typeof window === 'undefined') return;
  try {
    const all = getLikedPostsFromStorage();
    if (liked) all[postId] = true; else delete all[postId];
    localStorage.setItem(LIKED_KEY, JSON.stringify(all));
  } catch {}
};

export const isPostLikedLocally = (postId: string): boolean =>
  !!getLikedPostsFromStorage()[postId];

const parseResponse = async (response: Response, isArrayExpected = false) => {
  const text = await response.text();
  let data: any;
  try { data = text ? JSON.parse(text) : {}; }
  catch { data = { message: text }; }

  if (!response.ok) {
    throw new Error(data.message || data.error || `HTTP Error ${response.status}`);
  }

  if (isArrayExpected) {
    return Array.isArray(data)
      ? data
      : (data.data || data.items || data.posts || data.groups || data.messages ||
         data.packages || data.availability || data.appointments || []);
  }
  return data;
};

export const apiService = {

  // ==========================================
  // AUTHENTICATION API
  // ==========================================

  sendPasswordResetEmail: async (email: string, lang?: 'ar' | 'en') => {
    if (!email) throw new Error('البريد الإلكتروني مطلوب لإرسال رابط إعادة التعيين');
    const resolvedLang = lang ?? getCurrentLang();
    const response = await fetch(`${BASE_URL}/auth/forgot-password`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ email, lang: resolvedLang }),
    });
    return parseResponse(response);
  },

  getUsers: async () => {
    const response = await fetch(`${BASE_URL}/auth/users?t=${Date.now()}`, {
      headers: getAuthHeaders(), cache: 'no-store',
    });
    return parseResponse(response, true);
  },

  login: async (data: { email: string; password?: string; lang?: 'ar' | 'en' }) => {
    const resolvedLang: 'ar' | 'en' = data.lang ?? getCurrentLang();
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST', headers: getPublicHeaders(),
      body: JSON.stringify({ email: data.email, password: data.password, lang: resolvedLang }),
    });
    const result = await parseResponse(response);

    if (result?.token && result?.user && typeof window !== 'undefined') {
      const user     = result.user;
      const fullName = user.fullName || user.name || '';
      const role     = (user.role || user.userType || '').toLowerCase().trim();

      if (role === 'patient' || role === 'user' || role === '') {
        throw new Error('عذراً، هذه البوابة مخصصة للإدارة والأخصائيين فقط. يرجى استخدام تطبيق الهاتف.');
      }

      const langCode = mapLanguageCode(user.defaultLanguage || 'Arabic');
      const userInfo = {
        id: user.id, fullName, name: fullName,
        username: user.username || user.userName || '',
        email: user.email, role, defaultLanguage: langCode,
        instantSessionPrice: Number(user.instantSessionPrice ?? 0),
      };

      localStorage.setItem('auth_token', result.token);
      localStorage.setItem('user_info',  JSON.stringify(userInfo));
      setCookie('auth_token',  result.token);
      setCookie('user_info',   JSON.stringify(userInfo));
      setCookie('NEXT_LOCALE', langCode);
    }
    return result;
  },

  registerPatient: async (userData: {
    email: string; fullName: string; authUid?: string; password?: string;
  }) => {
    const response = await fetch(`${BASE_URL}/auth/register`, {
      method: 'POST', headers: getPublicHeaders(),
      body: JSON.stringify(userData), cache: 'no-store',
    });
    return parseResponse(response);
  },

  toggleUserStatus: async (id: string) => {
    const response = await fetch(`${BASE_URL}/auth/users/${id}/status`, {
      method: 'PATCH', headers: getAuthHeaders(), cache: 'no-store',
    });
    return parseResponse(response);
  },

  deleteUser: async (id: string) => {
    const response = await fetch(`${BASE_URL}/auth/users/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(), cache: 'no-store',
    });
    return parseResponse(response);
  },

  updateUser: async (id: string, data: {
    fullName?: string; email?: string; username?: string;
    gender?: string; country?: string; avatarUrl?: string; dob?: string;
  }) => {
    const response = await fetch(`${BASE_URL}/auth/users/${id}`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify(data), cache: 'no-store',
    });
    return parseResponse(response);
  },

  setUserPassword: async (userId: string, newPassword: string, lang?: 'ar' | 'en') => {
    const resolvedLang = lang ?? getCurrentLang();
    const response = await fetch(`${BASE_URL}/auth/users/${userId}/set-password`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ password: newPassword, lang: resolvedLang }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  bulkAddUsers: async (usersData: any[]) => {
    const response = await fetch(`${BASE_URL}/auth/users/bulk`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ users: usersData }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  sendActivationEmails: async (userIds: string[]) => {
    const response = await fetch(`${BASE_URL}/auth/users/send-activation`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ userIds }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  // ==========================================
  // SPECIALISTS API
  // ==========================================

  registerSpecialist: async (specData: {
    email: string; fullName: string; authUid?: string;
    specialty: string; password?: string; baseCurrency?: string; experienceYears?: number;
  }) => {
    const response = await fetch(`${BASE_URL}/auth/specialists/register`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify(specData), cache: 'no-store',
    });
    return parseResponse(response);
  },

  inviteSpecialist: async (specData: {
    email: string; fullName: string; specialty: string;
    baseCurrency: string; experienceYears: number;
  }) => {
    const response = await fetch(`${BASE_URL}/auth/specialists/invite`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify(specData), cache: 'no-store',
    });
    return parseResponse(response);
  },

  getSpecialists: async () => {
    const response = await fetch(`${BASE_URL}/auth/specialists-admin?t=${Date.now()}`, {
      headers: getAuthHeaders(),
    });
    return parseResponse(response, true);
  },

  getSpecialist: async (id: string) => {
    const response = await fetch(`${BASE_URL}/auth/specialists/${id}`, {
      headers: getAuthHeaders(),
    });
    return parseResponse(response);
  },

  toggleSpecialistStatus: async (id: string) => {
    const response = await fetch(`${BASE_URL}/auth/specialists/${id}/status`, {
      method: 'PATCH', headers: getAuthHeaders(), cache: 'no-store',
    });
    return parseResponse(response);
  },

  updateSpecialist: async (id: string, data: {
    fullName: string; email: string; specialty: string;
    baseCurrency: string; experienceYears: number;
  }) => {
    const response = await fetch(`${BASE_URL}/auth/specialists/${id}`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify(data), cache: 'no-store',
    });
    return parseResponse(response);
  },

  deleteSpecialist: async (id: string) => {
    if (!id) throw new Error('Invalid specialist ID');
    const response = await fetch(`${BASE_URL}/auth/specialists/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(), cache: 'no-store',
    });
    return parseResponse(response);
  },

  setSpecialistPassword: async (specialistId: string, newPassword: string, lang?: 'ar' | 'en') => {
    const resolvedLang = lang ?? getCurrentLang();
    const response = await fetch(`${BASE_URL}/auth/specialists/${specialistId}/set-password`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ password: newPassword, lang: resolvedLang }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  // ✅ الإصلاح: يستدعي /:id/admin-activate بدل /admin-activate
  adminActivateSpecialist: async (id: string, password: string) => {
    const lang = getCurrentLang();
    const response = await fetch(`${BASE_URL}/auth/specialists/${id}/admin-activate`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ password, lang }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  // ✅ جديد: يرسل الدعوة للأخصائي الموجود بدون إنشاء record جديد
  resendSpecialistInvite: async (id: string) => {
    const lang = getCurrentLang();
    const response = await fetch(`${BASE_URL}/auth/specialists/${id}/resend-invite`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ lang }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  getSpecialistProfile: async () => {
    const specialistId = getSpecialistId();
    if (!specialistId) throw new Error('User not found');
    const response = await fetch(`${BASE_URL}/specialists/${specialistId}`, {
      headers: getAuthHeaders(),
    });
    return parseResponse(response);
  },

  updateSpecialistProfile: async (data: any) => {
    const specialistId = getSpecialistId();
    if (!specialistId) throw new Error('User not found');

    if (typeof window !== 'undefined') {
      const userInfoStr = localStorage.getItem('user_info');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        if (data.fullName)        { userInfo.fullName = data.fullName; userInfo.name = data.fullName; }
        if (data.username)        userInfo.username = data.username;
        if (data.defaultLanguage) userInfo.defaultLanguage = mapLanguageCode(data.defaultLanguage);
        const updatedInfo = JSON.stringify(userInfo);
        localStorage.setItem('user_info', updatedInfo);
        setCookie('user_info', updatedInfo);
        if (data.defaultLanguage) setCookie('NEXT_LOCALE', userInfo.defaultLanguage);
      }
    }

    try {
      const response = await fetch(`${BASE_URL}/specialists/${specialistId}`, {
        method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(data),
      });
      return await parseResponse(response);
    } catch { return { success: true, savedLocally: true }; }
  },

  updateVisibilityStatus: async (data: {
    isOnLeave?: boolean; isInstantActive?: boolean; instantSessionPrice?: number;
  }) => {
    const specialistId = getSpecialistId();
    if (!specialistId) throw new Error('User not found');
    const response = await fetch(`${BASE_URL}/specialists/${specialistId}/status`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify(data), cache: 'no-store',
    });
    return parseResponse(response);
  },

  getSpecialistDashboardOverview: async () => {
    const specialistId = getSpecialistId();
    if (!specialistId) throw new Error('User not found');
    const response = await fetch(`${BASE_URL}/specialists/${specialistId}/dashboard-overview`, {
      headers: getAuthHeaders(),
    });
    return parseResponse(response);
  },

  createInstantMeeting: async (topic?: string) => {
    const specialistId = getSpecialistId();
    if (!specialistId) throw new Error('User not found');
    const response = await fetch(`${BASE_URL}/zoom/create-meeting`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ topic: topic || 'جلسة استشارة فورية', specialistId }),
    });
    return parseResponse(response);
  },

  endInstantSession: async (specialistId?: string) => {
    const id = specialistId || getSpecialistId();
    if (!id) throw new Error('User not found');
    const response = await fetch(`${BASE_URL}/zoom/end-session`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ specialistId: id }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  // ==========================================
  // POSTS API
  // ==========================================

  getPosts: async () => {
    const response = await fetch(`${BASE_URL}/posts`, { headers: getAuthHeaders() });
    return parseResponse(response, true);
  },

  getSpecialistPosts: async () => {
    const specialistId = getSpecialistId();
    if (!specialistId) throw new Error('User not found');
    const response = await fetch(`${BASE_URL}/posts/specialist/${specialistId}`, {
      headers: getAuthHeaders(),
    });
    return parseResponse(response, true);
  },

  likePost: async (postId: string): Promise<{ post: any; liked: boolean }> => {
    const user = getCurrentUserInfo();
    const response = await fetch(`${BASE_URL}/posts/${postId}/like`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ userName: user.displayName, userEmail: user.email }), cache: 'no-store',
    });
    const result      = await parseResponse(response);
    const serverLiked = typeof result.liked === 'boolean' ? result.liked : !isPostLikedLocally(postId);
    setLikedInStorage(postId, serverLiked);
    return { post: result.post ?? result, liked: serverLiked };
  },

  getInitialLikedPosts: (): Record<string, boolean> => getLikedPostsFromStorage(),

  getPostLikes: async (postId: string): Promise<{ likes: any[]; totalCount: number; trackedCount: number }> => {
    const response = await fetch(`${BASE_URL}/posts/${postId}/likes`, { headers: getAuthHeaders() });
    const data = await parseResponse(response);
    const normalizeLike = (like: any) => ({
      ...like,
      role:         like.role        || like.userType || 'patient',
      isAdmin:      like.isAdmin      ?? (like.role === 'admin'      || like.userType === 'admin'),
      isSpecialist: like.isSpecialist ?? (like.role === 'specialist' || like.userType === 'specialist'),
      fullName:     like.fullName || like.userName || like.name || '',
      email:        like.email    || like.userEmail || '',
    });
    if (Array.isArray(data)) {
      const likes = data.map(normalizeLike);
      return { likes, totalCount: likes.length, trackedCount: likes.length };
    }
    const rawLikes     = Array.isArray(data.likes) ? data.likes : [];
    const likes        = rawLikes.map(normalizeLike);
    const totalCount   = typeof data.totalCount   === 'number' ? data.totalCount   : likes.length;
    const trackedCount = typeof data.trackedCount === 'number' ? data.trackedCount : likes.length;
    return { likes, totalCount, trackedCount };
  },

  syncLikedStateFromServer: async (postIds: string[]): Promise<Record<string, boolean>> => {
    const user = getCurrentUserInfo();
    if (!user.id || postIds.length === 0) return getLikedPostsFromStorage();
    const likedMap: Record<string, boolean> = {};
    await Promise.all(postIds.map(async (postId) => {
      try {
        const response = await fetch(`${BASE_URL}/posts/${postId}/likes`, { headers: getAuthHeaders() });
        const data     = await parseResponse(response);
        const likes    = Array.isArray(data) ? data : (data.likes || []);
        const userLiked = likes.some((l: any) => l.id === user.id || l.userId === user.id);
        if (userLiked) likedMap[postId] = true;
      } catch {}
    }));
    if (typeof window !== 'undefined') localStorage.setItem(LIKED_KEY, JSON.stringify(likedMap));
    return likedMap;
  },

  recordPostView: async (postId: string) => {
    const response = await fetch(`${BASE_URL}/posts/${postId}/view`, {
      method: 'POST', headers: getAuthHeaders(), cache: 'no-store',
    });
    return parseResponse(response);
  },

  getPostViews: async (postId: string) => {
    const response = await fetch(`${BASE_URL}/posts/${postId}/views`, { headers: getAuthHeaders() });
    return parseResponse(response, true);
  },

  createPost: async (postData: { content: string }) => {
    const specialistId = getSpecialistId();
    const user         = getCurrentUserInfo();
    let authorName = 'Kalimni Official';
    let authorRole = 'System Admin';
    if (user.role === 'specialist') { authorName = user.displayName || user.fullName || 'أخصائي'; authorRole = 'Specialist'; }
    else if (user.role === 'admin') { authorName = user.displayName || 'Kalimni Official'; }
    const response = await fetch(`${BASE_URL}/posts`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ ...postData, specialistId, authorName, authorRole }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  deletePost: async (id: string) => {
    const response = await fetch(`${BASE_URL}/posts/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(), cache: 'no-store',
    });
    setLikedInStorage(id, false);
    return parseResponse(response);
  },

  deleteComment: async (commentId: string) => {
    const response = await fetch(`${BASE_URL}/posts/comments/${commentId}`, {
      method: 'DELETE', headers: getAuthHeaders(), cache: 'no-store',
    });
    return parseResponse(response);
  },

  addComment: async (postId: string, data: { text: string; userName?: string }) => {
    const user          = getCurrentUserInfo();
    const finalUserName = data.userName || user.displayName || user.fullName || 'مستخدم';
    const response      = await fetch(`${BASE_URL}/posts/${postId}/comments`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ ...data, userName: finalUserName }),
    });
    return parseResponse(response);
  },

  // ==========================================
  // GROUPS API
  // ==========================================

  getGroupCategories: async () => {
    const response = await fetch(`${BASE_URL}/groups/categories`, { headers: getAuthHeaders() });
    return parseResponse(response, true);
  },

  createGroupCategory: async (data: { nameKey: string; nameKeyAr?: string }) => {
    const response = await fetch(`${BASE_URL}/groups/categories`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return parseResponse(response);
  },

  updateGroupCategory: async (id: string, data: { nameKey?: string; nameKeyAr?: string }) => {
    const response = await fetch(`${BASE_URL}/groups/categories/${id}`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify(data), cache: 'no-store',
    });
    return parseResponse(response);
  },

  deleteGroupCategory: async (id: string) => {
    const response = await fetch(`${BASE_URL}/groups/categories/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(), cache: 'no-store',
    });
    return parseResponse(response);
  },

  getGroups: async (age?: string | number, gender?: string) => {
    let url = `${BASE_URL}/groups`;
    const params: string[] = [];
    if (age)    params.push(`age=${age}`);
    if (gender) params.push(`gender=${gender}`);
    if (params.length) url += `?${params.join('&')}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return parseResponse(response, true);
  },

  createGroup: async (groupData: {
    name: string; nameAr?: string; nameKey?: string; nameKeyAr?: string;
    categoryId: string; description?: string; descriptionAr?: string;
    targetGender?: string; minAge?: number | string; maxAge?: number | string;
  }) => {
    const response = await fetch(`${BASE_URL}/groups`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(groupData),
    });
    return parseResponse(response);
  },

  updateGroup: async (id: string, data: any) => {
    const response = await fetch(`${BASE_URL}/groups/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return parseResponse(response);
  },

  deleteGroup: async (id: string) => {
    const response = await fetch(`${BASE_URL}/groups/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    return parseResponse(response);
  },

  getGroupMessages: async (groupId: string) => {
    const response = await fetch(`${BASE_URL}/groups/${groupId}/messages?t=${Date.now()}`, {
      headers: getAuthHeaders(),
    });
    return parseResponse(response, true);
  },

  sendGroupMessage: async (
    groupId: string, text: string, role?: string,
    passedSenderName?: string, passedUserId?: string, replyToId?: string,
  ) => {
    const user = getCurrentUserInfo();
    let finalRole       = role;
    let finalUserId     = passedUserId;
    let finalSenderName = passedSenderName;
    if (!finalSenderName) finalSenderName = user.displayName || user.fullName || 'مستخدم';
    if (!finalRole)        finalRole       = user.role || 'patient';
    if (!finalUserId)      finalUserId     = user.id;
    const response = await fetch(`${BASE_URL}/groups/${groupId}/messages`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({
        text,
        role:       (finalRole || 'patient').toLowerCase().trim(),
        senderName: (finalSenderName || '').trim() || 'مستخدم',
        userId:     finalUserId || undefined,
        replyToId:  replyToId  || undefined,
      }),
      cache: 'no-store',
    });
    return parseResponse(response);
  },

  getGroupMembers: async (groupId: string) => {
    const response = await fetch(`${BASE_URL}/groups/${groupId}/members`, { headers: getAuthHeaders() });
    return parseResponse(response, true);
  },

  addGroupMember: async (groupId: string, email: string) => {
    const response = await fetch(`${BASE_URL}/groups/${groupId}/members`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ email }),
    });
    return parseResponse(response);
  },

  removeGroupMember: async (groupId: string, memberId: string) => {
    const response = await fetch(`${BASE_URL}/groups/${groupId}/members/${memberId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    return parseResponse(response);
  },

  toggleMessageReaction: async (messageId: string, userId: string, reactionType: string, userName?: string) => {
    const user = getCurrentUserInfo();
    let resolvedName = (userName || '').trim();
    if (!resolvedName || resolvedName === 'مستخدم' || resolvedName === 'User') {
      resolvedName = user.displayName || user.fullName || 'مستخدم';
    }
    const response = await fetch(`${BASE_URL}/groups/messages/${messageId}/reaction`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ userId, reactionType, userName: resolvedName || 'مستخدم' }),
      cache: 'no-store',
    });
    return parseResponse(response);
  },

  // ==========================================
  // NOTIFICATIONS API
  // ==========================================

  getNotificationsHistory: async () => {
    const response = await fetch(`${BASE_URL}/notifications`, { headers: getAuthHeaders() });
    return parseResponse(response, true);
  },

  sendNotification: async (data: { title: string; message: string; target: string; targetLabel: string }) => {
    const response = await fetch(`${BASE_URL}/notifications/send`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return parseResponse(response);
  },

  // ==========================================
  // PAYMENTS API
  // ==========================================

  getFinancialDashboard: async () => {
    const response = await fetch(`${BASE_URL}/payments/dashboard`, { headers: getAuthHeaders() });
    return parseResponse(response);
  },

  getSpecialistFinancials: async () => {
    const specialistId = getSpecialistId();
    if (!specialistId) throw new Error('User ID not found');
    const response = await fetch(`${BASE_URL}/payments/specialist/${specialistId}`, { headers: getAuthHeaders() });
    return parseResponse(response);
  },

  updatePaymentStatus: async (id: string, status: 'APPROVED' | 'REJECTED') => {
    const response = await fetch(`${BASE_URL}/payments/${id}/status`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify({ status }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  deletePayment: async (id: string) => {
    const response = await fetch(`${BASE_URL}/payments/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(), cache: 'no-store',
    });
    return parseResponse(response);
  },

  createManualPayment: async (data: {
    userId: string; specialistId?: string; amount: number;
    specialistPercentage: number; notes?: string;
  }) => {
    const response = await fetch(`${BASE_URL}/payments/manual`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return parseResponse(response);
  },

  // ==========================================
  // PACKAGES API
  // ==========================================

  getPackages: async () => {
    const response = await fetch(`${BASE_URL}/packages`, { headers: getAuthHeaders() });
    return parseResponse(response, true);
  },

  updatePackageStatus: async (id: string, status: string) => {
    const response = await fetch(`${BASE_URL}/packages/${id}/status`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify({ status }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  getSpecialistPackages: async (specialistId: string) => {
    const response = await fetch(`${BASE_URL}/packages/specialist/${specialistId}`, { headers: getAuthHeaders() });
    const data = await parseResponse(response);
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return { success: data.success ?? true, packages: data.packages ?? data.data ?? [] };
    }
    return { success: true, packages: Array.isArray(data) ? data : [] };
  },

  createPackage: async (data: any) => {
    const response = await fetch(`${BASE_URL}/packages`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const res = await parseResponse(response);
    return { success: res.success ?? true, package: res.package ?? res.data ?? res };
  },

  updatePackage: async (packageId: string, data: any) => {
    const response = await fetch(`${BASE_URL}/packages/${packageId}`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify(data), cache: 'no-store',
    });
    const res = await parseResponse(response);
    return { success: res.success ?? true, package: res.package ?? res.data ?? res };
  },

  deletePackage: async (packageId: string) => {
    const response = await fetch(`${BASE_URL}/packages/${packageId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    return parseResponse(response);
  },

  updateInstantPrice: async (specialistId: string, instantSessionPrice: number) => {
    const response = await fetch(`${BASE_URL}/specialists/${specialistId}/status`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify({ instantSessionPrice }), cache: 'no-store',
    });
    return parseResponse(response);
  },

  // ==========================================
  // APPOINTMENTS & AVAILABILITY API
  // ==========================================

  getSpecialistAvailability: async (specialistId: string) => {
    const response = await fetch(`${BASE_URL}/appointments/specialist/${specialistId}/availability`, { headers: getAuthHeaders() });
    const data = await parseResponse(response);
    if (Array.isArray(data)) return { success: true, availability: data };
    return { success: true, availability: data?.availability || data?.data || [] };
  },

  updateSpecialistAvailability: async (specialistId: string, schedule: any[]) => {
    const response = await fetch(`${BASE_URL}/appointments/specialist/${specialistId}/availability`, {
      method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ schedule }),
    });
    return parseResponse(response);
  },

  getSpecialistAppointments: async (specialistId: string) => {
    const response = await fetch(`${BASE_URL}/appointments/specialist/${specialistId}?t=${Date.now()}`, { headers: getAuthHeaders() });
    const data = await parseResponse(response);
    if (Array.isArray(data)) return { success: true, appointments: data };
    return { success: true, appointments: data?.appointments || data?.data || [] };
  },

  // ==========================================
  // SYSTEM LOGS & SETTINGS
  // ==========================================

  getLogs: async () => {
    const response = await fetch(`${BASE_URL}/auth/audit-logs?t=${Date.now()}`, {
      headers: getAuthHeaders(), cache: 'no-store',
    });
    return parseResponse(response, true);
  },

  getSettings: async () => {
    const response = await fetch(`${BASE_URL}/settings`, { headers: getAuthHeaders() });
    return parseResponse(response);
  },

  updateSettings: async (data: any) => {
    const response = await fetch(`${BASE_URL}/settings`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify(data), cache: 'no-store',
    });
    return parseResponse(response);
  },

  updateSpecialistPassword: async (data: { currentPassword: string; newPassword: string }) => {
    const response = await fetch(`${BASE_URL}/auth/change-password`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify(data), cache: 'no-store',
    });
    return parseResponse(response);
  },
};